import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';

/**
 * Uses localStorage to store and retrieve data as state
 * @param {string} key storage key
 * @param {object} initialValue initial value
 * @param {integer} maxAge in milliseconds
 * @param {function} expireCallback callback to fire if storage did expire 
 * @returns {[mixed, function]} state and setState
 */
function useLocalStorage( key, initialValue, maxAge = Infinity, expireCallback ) {

    const expirationKey = `${key}-expiration`;


    const loadFromLS = useCallback(() => {

        const ls = JSON.parse(localStorage.getItem(key)) ?? initialValue

        const isExpired = () => {
            const validUntil = parseInt(window.localStorage.getItem(expirationKey) ?? 0)
            return Date.now() >= validUntil
        }
    
        const handleExpirationCallback = () => {
            if ( typeof expireCallback === 'function') {
                expireCallback()
            }
        }

        if( isExpired() ) {
            logger('LocalStorage has expired, runing callback and setting initial values')
            localStorage.setItem( key, JSON.stringify(initialValue))
            localStorage.setItem( expirationKey, Date.now() + maxAge)
            handleExpirationCallback()
            return initialValue
        } else {
            logger('LocalStorage is ok')
            return ls
        }
    },[expirationKey, initialValue, key, maxAge, expireCallback])

    const [ storedValue, setStoredValue ] = useState(loadFromLS())

    useEffect(()=>{
        localStorage.setItem( key, JSON.stringify(storedValue))
        localStorage.setItem( expirationKey, Date.now() + maxAge)
    },[storedValue, key, maxAge, expirationKey])

    return [storedValue, setStoredValue]
}

export default useLocalStorage;